/**
 * Preserve URL Parameters
 * Automatically appends current URL parameters to all href links and window.location.href assignments
 */

(function() {
  'use strict';

  // Configuration: Parameters to exclude (e.g., internal tracking params you don't want to preserve)
  const EXCLUDE_PARAMS = []; //['_ga', '_gid', 'fbclid', 'gclid']; // Add any params you want to exclude

  /**
   * Get current URL parameters as a query string
   * @returns {string} Query string (without leading ?)
   */
  function getCurrentUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const params = new URLSearchParams();
    
    // On the homepage, `view` selects a split-test template (index.<suffix>.json). Never carry it to
    // other pages: Shopify would serve their default template instead of the one assigned in admin.
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const onHomepage = window.location.pathname.replace(/\/?$/, '/') === root;

    // Copy all params except excluded ones
    urlParams.forEach((value, key) => {
      if (onHomepage && key.toLowerCase() === 'view') {
        return;
      }
      if (!EXCLUDE_PARAMS.includes(key.toLowerCase())) {
        params.append(key, value);
      }
    });
    
    return params.toString();
  }

  /**
   * Merge URL parameters into an existing URL
   * @param {string} url - The URL to merge parameters into
   * @param {string} params - The parameters to merge (query string format)
   * @returns {string} URL with merged parameters
   */
  function mergeUrlParams(url, params) {
    if (!params || params.trim() === '') {
      return url;
    }

    try {
      // Handle relative URLs
      const isRelative = !url.match(/^https?:\/\//);
      const baseUrl = isRelative ? window.location.origin : '';
      const fullUrl = isRelative ? new URL(url, window.location.origin) : new URL(url);
      
      // Merge existing params with new params
      const existingParams = new URLSearchParams(fullUrl.search);
      const newParams = new URLSearchParams(params);
      
      // Add new params, but don't overwrite existing ones in the target URL
      newParams.forEach((value, key) => {
        if (!existingParams.has(key)) {
          existingParams.append(key, value);
        }
      });
      
      // Reconstruct URL
      const mergedParams = existingParams.toString();
      const separator = fullUrl.pathname.includes('?') ? '&' : (mergedParams ? '?' : '');
      const newUrl = fullUrl.origin + fullUrl.pathname + (mergedParams ? separator + mergedParams : '') + fullUrl.hash;
      
      // Return relative URL if original was relative
      return isRelative ? newUrl.replace(window.location.origin, '') : newUrl;
    } catch (e) {
      // If URL parsing fails, append params manually
      const separator = url.includes('?') ? '&' : '?';
      return url + separator + params;
    }
  }

  /**
   * Update href attribute of an element
   * @param {HTMLElement} element - Element to update
   */
  function updateElementHref(element) {
    const href = element.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return; // Skip anchors, javascript, mailto, tel links
    }
    
    const currentParams = getCurrentUrlParams();
    if (currentParams) {
      const newHref = mergeUrlParams(href, currentParams);
      if (newHref !== href) {
        element.setAttribute('href', newHref);
      }
    }
  }

  /**
   * Process all href links on the page
   */
  function processAllLinks() {
    const currentParams = getCurrentUrlParams();
    if (!currentParams) {
      return; // No params to preserve
    }

    // Process all <a> tags with href
    const links = document.querySelectorAll('a[href]');
    links.forEach(link => {
      updateElementHref(link);
    });

    // Process form actions
    const forms = document.querySelectorAll('form[action]');
    forms.forEach(form => {
      const action = form.getAttribute('action');
      if (action && !action.startsWith('#')) {
        const newAction = mergeUrlParams(action, currentParams);
        if (newAction !== action) {
          form.setAttribute('action', newAction);
        }
      }
    });
  }

    /**
   * Intercept window.location methods and assignments
   * Note: Direct assignment to window.location.href cannot be intercepted,
   * but we can intercept location.assign() and location.replace()
   */
  function interceptLocationHref() {
    // Override location.assign - refresh params on each call
    try {
      const originalAssign = window.location.assign.bind(window.location);
      window.location.assign = function(url) {
        const currentParams = getCurrentUrlParams();
        if (currentParams) {
          const newUrl = mergeUrlParams(url, currentParams);
          originalAssign(newUrl);
        } else {
          originalAssign(url);
        }
      };
    } catch (e) {
      // In modern browsers, location.assign is read-only
      // Fallback: We'll rely on navigateWithParams function instead
      console.debug('Cannot override location.assign (read-only property)');
    }

    // Override location.replace - refresh params on each call
    try {
      const originalReplace = window.location.replace.bind(window.location);
      window.location.replace = function(url) {
        const currentParams = getCurrentUrlParams();
        if (currentParams) {
          const newUrl = mergeUrlParams(url, currentParams);
          originalReplace(newUrl);
        } else {
          originalReplace(url);
        }
      };
    } catch (e) {
      // In modern browsers, location.replace is read-only
      // Fallback: We'll rely on navigateWithParams function instead
      console.debug('Cannot override location.replace (read-only property)');
    }

    // Intercept window.open calls - refresh params on each call
    const originalOpen = window.open;
    window.open = function(url, target, features) {
      if (typeof url === 'string' && url) {
        const currentParams = getCurrentUrlParams();
        if (currentParams) {
          const newUrl = mergeUrlParams(url, currentParams);
          return originalOpen.call(this, newUrl, target, features);
        }
      }
      return originalOpen.call(this, url, target, features);
    };
  }

  /**
   * Watch for dynamically added elements
   */
  function watchForNewElements() {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            // Check if it's a link
            if (node.tagName === 'A' && node.hasAttribute('href')) {
              updateElementHref(node);
            }
            // Check for links inside the added node
            const links = node.querySelectorAll ? node.querySelectorAll('a[href]') : [];
            links.forEach(link => updateElementHref(link));
            
            // Check for forms
            if (node.tagName === 'FORM' && node.hasAttribute('action')) {
              const action = node.getAttribute('action');
              if (action && !action.startsWith('#')) {
                const currentParams = getCurrentUrlParams();
                if (currentParams) {
                  const newAction = mergeUrlParams(action, currentParams);
                  node.setAttribute('action', newAction);
                }
              }
            }
            const forms = node.querySelectorAll ? node.querySelectorAll('form[action]') : [];
            forms.forEach(form => {
              const action = form.getAttribute('action');
              if (action && !action.startsWith('#')) {
                const currentParams = getCurrentUrlParams();
                if (currentParams) {
                  const newAction = mergeUrlParams(action, currentParams);
                  form.setAttribute('action', newAction);
                }
              }
            });
          }
        });
      });
    });

    // Only observe if body exists
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Intercept click events on links to ensure params are added even if href wasn't updated
   */
  function interceptLinkClicks() {
    document.addEventListener('click', function(e) {
      const link = e.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      const currentParams = getCurrentUrlParams();
      if (currentParams) {
        const newHref = mergeUrlParams(href, currentParams);
        if (newHref !== href) {
          e.preventDefault();
          // Use location.assign to preserve params
          window.location.assign(newHref);
        }
      }
    }, true); // Use capture phase to catch early
  }

  /**
   * Helper function for scripts to navigate with params preserved
   * Usage: window.navigateWithParams('/new-page')
   * This should be used instead of direct window.location.href assignment
   */
  window.navigateWithParams = function(url) {
    const currentParams = getCurrentUrlParams();
    if (currentParams) {
      const newUrl = mergeUrlParams(url, currentParams);
      window.location.href = newUrl;
    } else {
      window.location.href = url;
    }
  };

  /**
   * Initialize the URL parameter preservation
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        processAllLinks();
        interceptLocationHref();
        watchForNewElements();
        interceptLinkClicks();
      });
    } else {
      // DOM already ready
      processAllLinks();
      interceptLocationHref();
      watchForNewElements();
      interceptLinkClicks();
    }
  }

  // Initialize
  init();

  // Re-process on page visibility change (for SPAs or dynamic content)
  document.addEventListener('visibilitychange', function() {
    if (!document.hidden) {
      setTimeout(processAllLinks, 100);
    }
  });

})();

